import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadPromotionManagement} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  createZadPromotion,
  fetchZadPromotionById,
  fetchZadPromotions,
  updateZadPromotion,
  updateZadPromotionStatus,
} from './service'
import {FetchZadPromotionsResponse, ZadPromotionsState} from './types'

const initialState: ZadPromotionsState = {
  items: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadPromotionsState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadPromotionsState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertItem(state: ZadPromotionsState, item?: ZadPromotionManagement | null) {
  if (!item?._id) return
  const index = state.items.findIndex((row) => row._id === item._id)
  if (index >= 0) state.items[index] = item
  else state.items.unshift(item)
}

const zadPromotionsSlice = createSlice({
  name: 'zadPromotions',
  initialState,
  reducers: {
    clearZadPromotionsError: (state: ZadPromotionsState) => {
      clearError(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadPromotions.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadPromotions.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadPromotions.fulfilled,
      (state, action: PayloadAction<FetchZadPromotionsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.items = action.payload.items
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    const actionPending = (state: ZadPromotionsState) => {
      state.actionLoading = true
      clearError(state)
    }
    const actionRejected = (state: ZadPromotionsState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    }
    const actionFulfilled = (
      state: ZadPromotionsState,
      action: PayloadAction<{item: ZadPromotionManagement | null}>
    ) => {
      state.actionLoading = false
      clearError(state)
      upsertItem(state, action.payload.item)
    }

    ;[fetchZadPromotionById, createZadPromotion, updateZadPromotion, updateZadPromotionStatus].forEach(
      (thunk) => {
        builder.addCase(thunk.pending, actionPending)
        builder.addCase(thunk.rejected, actionRejected)
        builder.addCase(thunk.fulfilled, actionFulfilled)
      }
    )
  },
})

export default zadPromotionsSlice.reducer
export const actions = zadPromotionsSlice.actions
export {initialState as zadPromotionsInitialState}
